<template>
    <transition>
        <ais-instant-search
            :search-client="searchClient"
            :index-name="pagesIndex"
            :routing="routing"
        >
            <ais-configure :hits-per-page.camel="12" />

            <div class="mb-12 relative z-20">
                <div class="">
                    <div class="SearchBar max-w-4xl mx-auto px-5 lg:px-0">
                        <div class="flex justify-center mb-8">
                            <ais-search-box
                                v-slot="{ currentRefinement, isSearchStalled, refine }"
                                class="h-full lg:w-1/2"
                            >
                                <div
                                    class="flex bg-white bg-opacity-25 h-16 overflow-hidden lg:h-20 blur"
                                >
                                    <input
                                        class="flex-grow ml-8 mr-4 h5 text-white bg-transparent outline-none placeholder-white placeholder-uppercase"
                                        type="search"
                                        :value="currentRefinement"
                                        placeholder="Search for…"
                                        @input="refine($event.currentTarget.value)"
                                    />

                                    <img
                                        :hidden="!isSearchStalled"
                                        class="h-full"
                                        src="/themes/coralcoast/images/spinner.svg"
                                        alt=""
                                    />

                                    <span
                                        class="flex items-center justify-center w-16 border-l border-white border-opacity-25 lg:w-20 text-white"
                                    >
                                        <!-- prettier-ignore -->
                                        <svg class="w-4 h-4 stroke-current" viewBox="0 0 20 20">
                                            <g fill="none" fill-rule="evenodd" stroke-width="1.5" transform="translate(2 2)">
                                                <path stroke-linecap="round" d="M16 16L11 11"/>
                                                <circle cx="6.5" cy="6.5" r="6.5"/>
                                            </g>
                                        </svg>
                                    </span>
                                </div>
                            </ais-search-box>

                            <div class="hidden h-20 w-64 ml-4 lg:block blur">
                                <multiselect
                                    v-model="selectedIndex"
                                    :options="switcherOptions"
                                    :searchable="false"
                                    :close-on-select="true"
                                    :show-labels="false"
                                    placeholder="Pages"
                                    @input="indexSelected(selectedIndex)"
                                >
                                    <template slot="singleLabel" slot-scope="{ option }">
                                        {{ option.text }}
                                    </template>
                                    <template slot="option" slot-scope="{ option }">
                                        {{ option.text }}
                                    </template>
                                </multiselect>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div class="searchResults relative container mb-12 sm:mb-48">
                <div class="max-w-screen-lg mx-auto relative z-10">
                    <ais-stats v-slot="{ nbHits }">
                        <ais-hits
                            v-if="nbHits"
                            v-slot="{ items }"
                            class="grid gap-5 grid-square lg:pb-24"
                        >
                            <Page v-for="item in items" :key="item.objectID" :page="item" />
                        </ais-hits>

                        <p v-else class="text-center">
                            No results.
                        </p>
                    </ais-stats>

                    <ais-pagination
                        v-slot="{
                            currentRefinement,
                            pages,
                            isFirstPage,
                            isLastPage,
                            refine,
                            createURL
                        }"
                        :padding="2"
                    >
                        <div class="flex justify-center mt-16 small">
                            <a
                                v-if="!isFirstPage"
                                class="flex items-center justify-center w-12 h-12"
                                :href="createURL(currentRefinement - 1)"
                                @click.prevent="refine(currentRefinement - 1)"
                            >
                                <svg class="w-2 h-4 stroke-current" viewBox="0 0 6 10">
                                    <path
                                        fill="none"
                                        fill-rule="evenodd"
                                        stroke-linecap="round"
                                        stroke-width="1.5"
                                        d="M4.931 1.069L1.069 4.931m0 .138l3.862 3.862"
                                    />
                                </svg>
                            </a>

                            <a
                                v-for="page in pages"
                                :key="page"
                                class="flex items-center justify-center w-12 md:text-lg h-12 mx-1 hover:bg-blue-500 hover:text-white rounded-full"
                                :class="{
                                    'text-white bg-blue-500 rounded-full':
                                        page === currentRefinement
                                }"
                                :href="createURL(page)"
                                @click.prevent="refine(page)"
                            >
                                {{ page + 1 }}
                            </a>

                            <a
                                v-if="!isLastPage"
                                class="flex items-center justify-center w-12 h-12"
                                :href="createURL(currentRefinement + 1)"
                                @click.prevent="refine(currentRefinement + 1)"
                            >
                                <svg class="w-2 h-4 stroke-current" viewBox="0 0 6 10">
                                    <path
                                        fill="none"
                                        fill-rule="evenodd"
                                        stroke-linecap="round"
                                        stroke-width="1.5"
                                        d="M1.069 8.931l3.862-3.862m0-.138L1.069 1.069"
                                    />
                                </svg>
                            </a>
                        </div>
                    </ais-pagination>
                </div>
            </div>
        </ais-instant-search>
    </transition>
</template>

<script>
import algoliasearch from 'algoliasearch/lite';
import Page from '@/components/Page.vue';
import { routing } from '@/routing';
import Multiselect from 'vue-multiselect';

export default {
    components: {
        Page,
        Multiselect
    },

    props: {
        productsIndex: {
            type: String,
            required: true
        },
        pagesIndex: {
            type: String,
            required: true
        }
    },

    data() {
        return {
            searchClient: algoliasearch(window.algolia.id, window.algolia.key),
            routing: routing(this.pagesIndex),
            selectedIndex: { value: this.pagesIndex, text: 'Pages' },
            switcherOptions: [
                { value: this.productsIndex, text: 'Listings' },
                { value: this.pagesIndex, text: 'Pages' }
            ]
        };
    },

    methods: {
        indexSelected(index) {
            this.$emit('switch', index.value);
        }
    }
};
</script>

<style scoped>
.subitem-wrapper {
    &::before,
    &::after {
        margin: auto;
        content: '';
    }
}
</style>
